import { useState, useEffect } from 'react'

/**
 * Hook personalizado para manejar llamadas a API
 * @param {Function} asyncFunction - Función asíncrona que retorna datos
 * @param {Array} dependencies - Dependencias para re-ejecutar la función
 * @returns {Object} - { data, loading, error, refetch }
 */
export const useFetch = (asyncFunction, dependencies = []) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
            
      const result = await asyncFunction()
      setData(result)
    } catch (err) {
      setError(err)
      console.error('useFetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, dependencies)

  const refetch = () => {
    fetchData()
  }

  return {
    data,
    loading,
    error,
    refetch
  }
}

/**
 * Hook para manejar carga de productos
 * @param {Function} serviceFunction - Función del servicio a llamar
 * @param {Array} params - Parámetros para la función
 * @returns {Object} - { products, loading, error, refetch }
 */
export const useProducts = (serviceFunction, ...params) => {
  const { data, loading, error, refetch } = useFetch(
    () => serviceFunction(...params),
    params
  )

  return {
    products: data?.data?.products || [],
    totalProducts: data?.data?.total || 0,
    pagination: {
      page: data?.data?.page || 1,
      totalPages: data?.data?.totalPages || 1
    },
    loading,
    error,
    refetch
  }
}

/**
 * Hook para manejar carga de categorías
 * @param {Function} serviceFunction - Función del servicio a llamar
 * @returns {Object} - { categories, loading, error, refetch }
 */
export const useCategories = (serviceFunction) => {
  const { data, loading, error, refetch } = useFetch(serviceFunction)

  return {
    categories: data?.data || [],
    loading,
    error,
    refetch
  }
}

export default useFetch